import React from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Info } from 'lucide-react';
import { TNaerasDashboardReservoir, TNaerasPanel, TNaerasReservoirDay } from '@/data/types';
import PanelCard from './PanelCard';
import { COLORS, MISSING, isNum, m3, metres, mwh, num, pct, shortDate, signed } from '../utils/format';

interface ReservoirPanelProps {
  panel: TNaerasPanel<TNaerasDashboardReservoir> | undefined;
}

/** The reservoir as a whole is neither hybrid nor hydro, so it takes neither colour. */
const TOTAL = COLORS.other;

/**
 * Hydro water can come back negative: the pool is bookkeeping and isn't capped
 * by the reservoir. How to draw that is still open with the client, so until
 * it is agreed it is drawn as 0 and the card says why.
 */
const drawnHydro = (value: number | null) => (isNum(value) ? Math.max(0, value) : null);

const SERIES = [
  { key: 'volume_m3', name: 'Ταμιευτήρας', color: TOTAL },
  { key: 'pool_m3', name: 'Hybrid Pool', color: COLORS.hybrid },
  { key: 'hydro_m3', name: 'Υδροηλεκτρικό νερό', color: COLORS.hydro },
] as const;

const DayTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;

  const day: TNaerasReservoirDay = payload[0].payload.day;

  return (
    <div className="rounded-2xl border border-slate-100 bg-white/95 p-3 shadow-xl backdrop-blur-md">
      <p className="mb-1 text-xs font-bold text-slate-800">
        {label} · {metres(day.level_m)}
      </p>
      {SERIES.map((series) => (
        <div key={series.key} className="flex items-center gap-2 text-xs">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: series.color }} />
          <span className="font-medium text-slate-600">{series.name}:</span>
          <span className="font-extrabold text-slate-900">{m3(day[series.key])}</span>
        </div>
      ))}
    </div>
  );
};

/** One KPI block: a title, then rows of label/value. */
const KpiBlock: React.FC<{
  title: string;
  color: string;
  rows: { label: string; value: string }[];
}> = ({ title, color, rows }) => (
  <div className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
    <div className="mb-2 flex items-center gap-2">
      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
      <span className="text-xs font-bold tracking-wider text-slate-700 uppercase">{title}</span>
    </div>
    <dl className="space-y-1">
      {rows.map((row) => (
        <div key={row.label} className="flex items-baseline justify-between gap-3 text-xs">
          <dt className="text-slate-500">{row.label}</dt>
          <dd className="font-mono font-bold text-slate-900">{row.value}</dd>
        </div>
      ))}
    </dl>
  </div>
);

/** Today's reservoir as one stacked bar inside the capacity outline. */
const TodayBar: React.FC<{ today: TNaerasReservoirDay; capacity: number }> = ({ today, capacity }) => {
  const share = (value: number | null) =>
    isNum(value) && capacity > 0 ? `${Math.min(100, (value / capacity) * 100)}%` : '0%';

  return (
    <div className="flex h-full flex-col items-center">
      <span className="mb-2 text-xs font-bold text-slate-500">Χωρητικότητα {m3(capacity)}</span>
      <div className="relative flex w-28 flex-1 flex-col-reverse overflow-hidden rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50">
        <div
          className="w-full"
          style={{ height: share(today.pool_m3), backgroundColor: COLORS.hybrid }}
          title={`Hybrid Pool: ${m3(today.pool_m3)}`}
        />
        <div
          className="w-full"
          style={{ height: share(drawnHydro(today.hydro_m3)), backgroundColor: COLORS.hydro }}
          title={`Υδροηλεκτρικό νερό: ${m3(today.hydro_m3)}`}
        />
      </div>
      <span className="mt-2 text-xs font-bold text-slate-700">Σήμερα · {metres(today.level_m)}</span>
    </div>
  );
};

/**
 * §2.4 — the Προεσπέρα reservoir at 00:00 for the last 7 days, split into the
 * Hybrid Pool (pumped water) and hydro water. The only chart in m³.
 */
export const ReservoirPanel: React.FC<ReservoirPanelProps> = ({ panel }) => (
  <PanelCard
    title="Ταμιευτήρας & Hybrid Pool"
    subtitle="Ταμιευτήρας Προεσπέρας στις 00:00, τελευταίες 7 ημέρες"
    panel={panel}
    delay={0.3}
  >
    {(reservoir) => {
      const days = reservoir.days;
      const today = days[days.length - 1];
      const yesterday = days[days.length - 2];

      if (!today) {
        return <p className="text-sm text-gray-500">{MISSING}</p>;
      }

      const points = days.map((day, index) => ({
        label: index === days.length - 1 ? 'Σήμερα' : shortDate(day.date),
        volume_m3: day.volume_m3,
        pool_m3: day.pool_m3,
        hydro_m3: drawnHydro(day.hydro_m3),
        day,
      }));

      const hydroClipped = days.some((day) => isNum(day.hydro_m3) && day.hydro_m3 < 0);
      const delta = (a: number | null, b: number | null | undefined) => (isNum(a) && isNum(b) ? a - b : null);

      // A single day is no history to draw; the client asked for "Σήμερα" alone then
      const hasHistory = days.length > 1;

      return (
        <>
          <div className={`grid grid-cols-1 gap-6 ${hasHistory ? 'lg:grid-cols-[1fr_auto]' : ''}`}>
            {hasHistory && (
              <div>
                <h4 className="mb-2 text-sm font-bold text-slate-700">Εξέλιξη</h4>
                <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={points} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis
                        dataKey="label"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#64748b', fontSize: 11, fontWeight: 600 }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 600 }}
                        tickFormatter={(v) => num(v, 0)}
                        width={64}
                      />
                      <Tooltip content={<DayTooltip />} />
                      {SERIES.map((series) => (
                        <Line
                          key={series.key}
                          dataKey={series.key}
                          name={series.name}
                          stroke={series.color}
                          strokeWidth={2.5}
                          dot={{ r: 3.5, fill: series.color, strokeWidth: 0 }}
                          connectNulls={false}
                          isAnimationActive={false}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-2 flex flex-wrap gap-4 text-[11px] font-semibold text-slate-500">
                  {SERIES.map((series) => (
                    <span key={series.key} className="flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: series.color }} />
                      {series.name} (m³)
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className={hasHistory ? 'h-72 lg:h-auto' : 'h-72'}>
              <TodayBar today={today} capacity={reservoir.capacity_m3} />
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-3">
            <KpiBlock
              title="Ταμιευτήρας"
              color={TOTAL}
              rows={[
                { label: 'Ενέργεια', value: mwh(today.energy_mwh) },
                { label: 'Όγκος', value: m3(today.volume_m3) },
                { label: 'Στάθμη', value: metres(today.level_m) },
                { label: '% χωρητικότητας', value: pct(today.volume_m3, reservoir.capacity_m3) },
              ]}
            />
            <KpiBlock
              title="Hybrid Pool"
              color={COLORS.hybrid}
              rows={[
                { label: 'Ενέργεια', value: mwh(today.pool_mwh) },
                { label: 'Όγκος', value: m3(today.pool_m3) },
                { label: '% ταμιευτήρα', value: pct(today.pool_m3, today.volume_m3) },
                {
                  label: 'Δ από χθες',
                  value: deltaText(
                    delta(today.pool_mwh, yesterday?.pool_mwh),
                    delta(today.pool_m3, yesterday?.pool_m3),
                  ),
                },
              ]}
            />
            <KpiBlock
              title="Υδροηλεκτρικό νερό"
              color={COLORS.hydro}
              rows={[
                // No reservoir energy means no hydro MWh either; m³ still holds
                { label: 'Ενέργεια', value: isNum(today.energy_mwh) ? mwh(today.hydro_mwh) : MISSING },
                { label: 'Όγκος', value: m3(today.hydro_m3) },
                { label: '% ταμιευτήρα', value: pct(today.hydro_m3, today.volume_m3) },
                {
                  label: 'Δ από χθες',
                  value: deltaText(
                    isNum(today.energy_mwh) ? delta(today.hydro_mwh, yesterday?.hydro_mwh) : null,
                    delta(today.hydro_m3, yesterday?.hydro_m3),
                  ),
                },
              ]}
            />
          </div>

          {hydroClipped && (
            <div className="mt-4 flex items-center gap-2.5 rounded-2xl border border-amber-200/80 bg-amber-50/80 p-3.5 text-xs text-amber-900">
              <Info className="h-4 w-4 shrink-0 text-amber-600" />
              <p>Το Hybrid Pool ξεπερνά τον όγκο του ταμιευτήρα — το υδροηλεκτρικό νερό εμφανίζεται ως 0.</p>
            </div>
          )}
        </>
      );
    }}
  </PanelCard>
);

/** "+1,20 MWh · +1.081 m³", leaving out whichever side is missing. */
function deltaText(mwhDelta: number | null, m3Delta: number | null): string {
  const parts = [
    isNum(mwhDelta) ? `${signed(mwhDelta)} MWh` : null,
    isNum(m3Delta) ? `${signed(m3Delta, 0)} m³` : null,
  ].filter(Boolean);

  return parts.length ? parts.join(' · ') : MISSING;
}

export default ReservoirPanel;
